import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendUp, Circle } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Motion";

export default function MetricsPreview({ content }) {
  const m = content?.metrics || {};
  const stats = m.stats || [];
  const chart = m.chart || [];

  return (
    <section id="metricas" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <Reveal>
        <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-4">Tu tablero de control</h4>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink max-w-3xl leading-[1.05]">{m.heading}</h2>
        <p className="font-sans text-base md:text-lg text-subtle mt-6 max-w-xl">{m.subtitle}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 rounded-3xl border border-border bg-white overflow-hidden">
          {/* window bar */}
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border bg-stone-50">
            <Circle size={11} weight="fill" className="text-stone-300" />
            <Circle size={11} weight="fill" className="text-stone-300" />
            <Circle size={11} weight="fill" className="text-stone-300" />
            <span className="ml-4 font-sans text-xs tracking-wide text-faint">dommar.ai / panel · datos de ejemplo</span>
          </div>

          <div className="p-6 md:p-10 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              {stats.map((st, i) => (
                <motion.div
                  key={i}
                  data-testid={`metric-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="rounded-2xl border border-border p-5 flex flex-col justify-between min-h-[120px]"
                >
                  <span className="font-sans text-xs tracking-wide text-faint leading-snug">{st.label}</span>
                  <span className="font-serif text-4xl md:text-5xl text-ink mt-4">{st.value}</span>
                </motion.div>
              ))}
            </div>

            <div className="lg:col-span-7 rounded-2xl border border-border p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-sans text-xs tracking-wide text-faint">Crecimiento mensual</p>
                  <p className="font-serif text-2xl text-ink">Impacto acumulado</p>
                </div>
                <span className="flex items-center gap-1.5 font-sans text-sm text-ink bg-stone-100 px-3 py-1.5 rounded-full">
                  <TrendUp size={15} weight="bold" /> +279%
                </span>
              </div>
              <div className="h-[240px] w-full" style={{ minHeight: 240 }}>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={chart} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="metricFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#111111" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#111111" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#EDEDED" />
                    <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#888", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ border: "1px solid #E5E5E5", borderRadius: 12, fontFamily: "Manrope", fontSize: 13 }}
                      labelStyle={{ color: "#111" }}
                      cursor={{ stroke: "#D1CEBF" }}
                    />
                    <Area type="monotone" dataKey="value" stroke="#111111" strokeWidth={2.5} fill="url(#metricFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
